import type { Http, Query } from './http';
import type {
  ActivityLocations,
  Alert,
  AlertReport,
  AlertSetting,
  AlertStatus,
  AlertType,
  Assignment,
  Device,
  DeviceInput,
  ExportFormat,
  Geofence,
  GeofenceEvent,
  GeofenceInput,
  GeofenceReportRow,
  Language,
  LoginResponse,
  Notification,
  NotificationStatus,
  Page,
  PageQuery,
  ReportType,
  ReportWindow,
  Role,
  Status,
  TrackingOverview,
  TrackingStatus,
  TrackingVehicle,
  Trip,
  TripReportRow,
  TripRoutePoint,
  TripStop,
  User,
  Vehicle,
  VehicleActivityRow,
  VehicleInput,
  VehicleType,
} from './types';

const q = (o: object) => o as Query;

export interface UserInput {
  username: string;
  fullName: string;
  email: string;
  phone?: string | null;
  role: Role;
  notes?: string | null;
  language?: Language | null;
}

/** Every route of the legacy API, typed. */
export function createApi(http: Http) {
  const get = <T>(path: string, query?: object) => http.request<T>('GET', path, { query: query ? q(query) : undefined });
  const post = <T>(path: string, body?: unknown) => http.request<T>('POST', path, { body });
  const put = <T>(path: string, body?: unknown) => http.request<T>('PUT', path, { body });
  const patch = <T>(path: string, body?: unknown) => http.request<T>('PATCH', path, { body });
  const del = <T>(path: string) => http.request<T>('DELETE', path);

  return {
    auth: {
      login: (username: string, password: string) =>
        http.request<LoginResponse>('POST', '/api/auth/login', { body: { username, password }, auth: false }),
    },

    profile: {
      get: () => get<User>('/api/profile'),
      update: (body: { fullName: string; email: string; phone?: string | null }) => put<User>('/api/profile', body),
      setLanguage: (language: Language) => patch<User>('/api/profile/language', { language }),
      changePassword: (currentPassword: string, newPassword: string) => put<void>('/api/profile/password', { currentPassword, newPassword }),
      uploadImage: (file: File) => {
        const form = new FormData();
        form.append('file', file);
        return http.request<User>('POST', '/api/profile/image', { form });
      },
      deleteImage: () => del<User>('/api/profile/image'),
    },

    users: {
      list: (query: PageQuery & { search?: string; role?: Role | ''; status?: Status | '' } = {}) => get<Page<User>>('/api/users', query),
      get: (id: number) => get<User>(`/api/users/${id}`),
      create: (body: UserInput & { password: string }) => post<User>('/api/users', body),
      update: (id: number, body: UserInput) => put<User>(`/api/users/${id}`, body),
      setStatus: (id: number, status: Status) => patch<User>(`/api/users/${id}/status`, { status }),
      setLanguage: (id: number, language: Language) => patch<User>(`/api/users/${id}/language`, { language }),
    },

    vehicles: {
      list: (query: PageQuery & { search?: string; vehicleType?: VehicleType | ''; status?: Status | '' } = {}) =>
        get<Page<Vehicle>>('/api/vehicles', query),
      get: (id: number) => get<Vehicle>(`/api/vehicles/${id}`),
      create: (body: VehicleInput) => post<Vehicle>('/api/vehicles', body),
      update: (id: number, body: VehicleInput) => put<Vehicle>(`/api/vehicles/${id}`, body),
      setStatus: (id: number, status: Status) => patch<Vehicle>(`/api/vehicles/${id}/status`, { status }),
    },

    devices: {
      list: (query: PageQuery & { search?: string; model?: string; networkProvider?: string; status?: Status | '' } = {}) =>
        get<Page<Device>>('/api/devices', query),
      get: (id: number) => get<Device>(`/api/devices/${id}`),
      create: (body: DeviceInput) => post<Device>('/api/devices', body),
      update: (id: number, body: DeviceInput) => put<Device>(`/api/devices/${id}`, body),
      setStatus: (id: number, status: Status) => patch<Device>(`/api/devices/${id}/status`, { status }),
    },

    assignments: {
      list: (query: PageQuery & { search?: string } = {}) => get<Page<Assignment>>('/api/assignments', query),
      create: (vehicleId: number, deviceId: number) => post<Assignment>('/api/assignments', { vehicleId, deviceId }),
      /** Soft delete: the assignment becomes INACTIVE. */
      remove: (id: number) => del<void>(`/api/assignments/${id}`),
    },

    geofences: {
      list: (query: PageQuery = {}) => get<Page<Geofence>>('/api/geofences', query),
      get: (id: number) => get<Geofence>(`/api/geofences/${id}`),
      create: (body: GeofenceInput) => post<Geofence>('/api/geofences', body),
      update: (id: number, body: GeofenceInput) => put<Geofence>(`/api/geofences/${id}`, body),
      /** Hard delete, or INACTIVE when events reference it. */
      remove: (id: number) => del<void>(`/api/geofences/${id}`),
      events: (query: { vehicleId?: number | null; geofenceId?: number | null; startDate?: string; endDate?: string } = {}) =>
        get<GeofenceEvent[]>('/api/geofence-events', query),
    },

    alerts: {
      list: (
        query: PageQuery & { vehicleId?: number | null; alertType?: AlertType | ''; status?: AlertStatus | ''; startDate?: string; endDate?: string } = {},
      ) => get<Page<Alert>>('/api/alerts', query),
      get: (id: number) => get<Alert>(`/api/alerts/${id}`),
      setStatus: (id: number, status: AlertStatus) => patch<Alert>(`/api/alerts/${id}/status`, { status }),
      settings: () => get<AlertSetting[]>('/api/settings/alert-notifications'),
      updateSettings: (settings: AlertSetting[]) => put<AlertSetting[]>('/api/settings/alert-notifications', settings),
    },

    notifications: {
      list: (status?: NotificationStatus) => get<Notification[]>('/api/notifications', { status }),
      unreadCount: () => get<{ count: number }>('/api/notifications/unread-count'),
      markRead: (id: number) => patch<void>(`/api/notifications/${id}/read`),
    },

    trips: {
      list: (query: PageQuery & { vehicleId?: number | null; startDate?: string; endDate?: string } = {}) => get<Page<Trip>>('/api/trips', query),
      get: (id: number) => get<Trip>(`/api/trips/${id}`),
      route: (id: number) => get<TripRoutePoint[]>(`/api/trips/${id}/route`),
      stops: (id: number) => get<TripStop[]>(`/api/trips/${id}/stops`),
    },

    tracking: {
      vehicles: () => get<TrackingVehicle[]>('/api/tracking/vehicles'),
      overview: (status?: TrackingStatus) => get<TrackingOverview>('/api/tracking/overview', { status }),
      vehicle: (id: number) => get<TrackingVehicle>(`/api/tracking/vehicles/${id}`),
    },

    reports: {
      trips: (w: ReportWindow) => get<TripReportRow[]>('/api/reports/trips', w),
      vehicleActivity: (w: ReportWindow) => get<VehicleActivityRow[]>('/api/reports/vehicle-activity', w),
      vehicleActivityLocations: (w: ReportWindow & { vehicleId: number }) => get<ActivityLocations>('/api/reports/vehicle-activity/locations', w),
      alerts: (w: ReportWindow & { alertType?: AlertType | '' }) => get<AlertReport>('/api/reports/alerts', w),
      geofences: (w: ReportWindow & { geofenceId?: number | null }) => get<GeofenceReportRow[]>('/api/reports/geofences', w),
      /** Server-side export (CSV / Excel / PDF): the file and the name the API gave it. */
      async export(type: ReportType, format: ExportFormat, w: ReportWindow & { geofenceId?: number | null; alertType?: AlertType | '' }) {
        const res = await http.raw('GET', `/api/reports/${type}/export`, { query: q({ ...w, format }) });
        const disposition = res.headers.get('content-disposition') ?? '';
        const filename = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? `${type}_report.${format === 'excel' ? 'xlsx' : format}`;
        return { blob: await res.blob(), filename };
      },
    },
  };
}

export type VlmApi = ReturnType<typeof createApi>;
